
import React from 'react';
import { PokemonCard } from '../types';
import { Star, TrendingUp, Plus, Check } from 'lucide-react';

interface Props {
  card: PokemonCard;
  onClick: (card: PokemonCard) => void;
  onQuickAdd: (card: PokemonCard) => void;
  isFavorite?: boolean;
}

const getRarityStyles = (rarity: string = '') => {
  const r = rarity.toLowerCase();
  if (r.includes('secret') || r.includes('rainbow')) {
    return {
      bg: 'bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500',
      text: 'text-white',
      glow: 'shadow-[0_0_15px_rgba(236,72,153,0.4)]',
      animate: 'animate-pulse'
    };
  }
  if (r.includes('ultra') || r.includes('full art') || r.includes('vmax')) {
    return {
      bg: 'bg-red-600',
      text: 'text-white',
      glow: 'shadow-[0_0_12px_rgba(220,38,38,0.5)]',
      animate: ''
    };
  }
  if (r.includes('rare')) {
    return {
      bg: 'bg-yellow-500',
      text: 'text-slate-950',
      glow: 'shadow-[0_0_8px_rgba(234,179,8,0.3)]',
      animate: ''
    };
  }
  if (r.includes('uncommon')) {
    return {
      bg: 'bg-blue-600',
      text: 'text-white',
      glow: '',
      animate: ''
    };
  }
  return {
    bg: 'bg-slate-700',
    text: 'text-slate-300',
    glow: '',
    animate: ''
  };
};

const getMarketPrice = (prices: any) => {
  if (!prices) return null;
  // Priority order for display: Holofoil > Normal > Reverse Holofoil
  const priorities = ['holofoil', 'normal', 'reverseHolofoil', '1stEditionHolofoil'];
  
  for (const p of priorities) {
    if (prices[p]?.market) return prices[p].market;
  }
  
  // Fallback to first available
  const firstKey = Object.keys(prices)[0];
  return prices[firstKey]?.market;
};

const PokemonCardItem: React.FC<Props> = ({ card, onClick, onQuickAdd, isFavorite }) => {
  const rarityStyle = getRarityStyles(card.rarity);
  const marketPrice = getMarketPrice(card.tcgplayer?.prices);
  
  const releaseYear = card.set.releaseDate ? new Date(card.set.releaseDate).getFullYear() : '';
  const quantity = card.quantity || 1;
  const condition = card.condition || 9;

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuickAdd(card);
  };

  return (
    <div 
      onClick={() => onClick(card)}
      className={`group relative cursor-pointer transform transition-all duration-300 hover:-translate-y-2 ${rarityStyle.glow}`}
    >
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${rarityStyle.bg.includes('gradient') ? 'from-pink-500 to-indigo-500' : 'from-blue-500 to-red-500'} rounded-xl blur opacity-25 group-hover:opacity-60 transition duration-300`}></div>
      <div className="relative bg-slate-900 rounded-xl overflow-hidden border border-slate-800 shadow-xl h-full flex flex-col">
        <div className="relative">
          <img 
            src={card.images.small} 
            alt={card.name} 
            className="w-full h-auto aspect-[2.5/3.5] object-contain p-2"
            loading="lazy"
          />
          
          {/* Price Badge */}
          {marketPrice && (
            <div className="absolute top-3 left-3 bg-slate-950/90 backdrop-blur-md border border-green-500/30 px-2 py-1.5 rounded-lg flex items-center gap-1.5 shadow-xl z-10">
              <TrendingUp className="w-3 h-3 text-green-400" />
              <span className="text-white font-mono font-bold text-[10px] tracking-tight">
                ${marketPrice.toFixed(2)}
              </span>
            </div>
          )}
          
          {/* Vault Info Badges */}
          {isFavorite && (
              <div className="absolute bottom-3 left-3 z-10 flex gap-1">
                 {/* Quantity Badge */}
                 {quantity > 1 && (
                    <div className="bg-yellow-500 text-slate-950 font-black text-xs px-2 py-1 rounded-md shadow-lg border border-yellow-400">
                        x{quantity}
                    </div>
                 )}
                 {/* Condition Badge */}
                 <div className={`text-xs px-2 py-1 rounded-md shadow-lg border font-black ${condition >= 9 ? 'bg-green-600 text-white border-green-500' : condition >= 7 ? 'bg-blue-600 text-white border-blue-500' : 'bg-slate-700 text-slate-300 border-slate-600'}`}>
                    {condition.toFixed(1)}
                 </div>
              </div>
          )}

          {/* Quick Add / Favorite Button (Top Right) */}
          <button 
            onClick={handleQuickAdd}
            className={`absolute top-3 right-3 z-20 h-8 w-8 flex items-center justify-center rounded-lg shadow-xl transition-all active:scale-90 border ${
              isFavorite 
                ? 'bg-yellow-400 border-yellow-300 text-slate-950' 
                : 'bg-white border-white text-slate-950 hover:bg-slate-200'
            }`}
            title={isFavorite ? "Remove from Vault" : "Add to Vault"}
          >
            {isFavorite ? <Star className="w-4 h-4 fill-slate-950" /> : <Plus className="w-5 h-5" />}
          </button>
        </div>
        
        {/* Info Section */}
        <div className="p-3 bg-slate-900/80 backdrop-blur-sm mt-auto relative border-t border-slate-800">
          <div className="flex justify-between items-center mb-1 pr-1">
            <h3 className="font-bold text-slate-100 truncate text-sm">{card.name}</h3>
            {card.hp && <span className="text-[10px] font-bold text-red-400 shrink-0 ml-2">HP {card.hp}</span>}
          </div>
          
          <div className="flex flex-wrap gap-1 items-center mb-2">
            {card.types?.map(type => (
              <span key={type} className="px-1.5 py-0.5 rounded bg-slate-800 text-[9px] uppercase font-bold text-slate-400">
                {type}
              </span>
            ))}
            <span className={`px-1.5 py-0.5 rounded ${rarityStyle.bg} ${rarityStyle.text} text-[9px] uppercase font-black ml-auto ${rarityStyle.animate}`}>
              {card.rarity || 'Common'}
            </span>
          </div>
          
          {/* Footer Metadata */}
          <div className="flex justify-between items-center pt-2 border-t border-slate-800/50">
             <div className="flex items-center gap-1 text-[9px] text-slate-500 font-bold uppercase truncate max-w-[70%]">
                <span className="truncate">{card.set.name}</span>
             </div>
             <div className="text-[9px] text-slate-600 font-mono font-bold">
               {releaseYear}
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PokemonCardItem;
